/**
 * PATH: packages/ui/src/kanban/components/kanbanCardFooterSignals.ts
 * FILE_ID: UI-KANBAN-CARD-FOOTER-SIGNALS-001
 * PURPOSE: Derive footer counts and whether the triage strip should paint.
 * ROLE IN ARCHITECTURE: Pure I→O for KanbanCardFooter. Hide omits chrome.
 * MAIN EXPORTS: CARD_FACE_FOOTER_FIELD_IDS, FooterFieldVisibility,
 *   readFooterSignals, footerFieldOn
 * NON-RESPONSIBILITIES: Does not render or persist.
 * NOTES FOR FUTURE AI: Prefer attachment_count over the UUID array. Off
 *   zeros that signal so the strip can disappear without deleting card data.
 */
import type { ReactNode } from 'react'

import type { KanbanEmailCardMeta, KanbanMember } from '../api'
import type { Card as KanbanCardModel } from '../types'
import { cardReferenceChip } from './kanbanCardVisuals'

export const CARD_FACE_FOOTER_FIELD_IDS = [
  'priority',
  'personalCopy',
  'mail',
  'replies',
  'description',
  'attachments',
  'checklist',
  'comments',
  'subcards',
  'rollup',
  'workbook',
  'related',
  'unseen',
  'merged',
  'presence',
  'due',
  'actionDate',
  'previousDue',
  'previousAction',
  'snooze',
  'assignees',
  'jobRef',
  'mailbox',
] as const

export type FooterFieldId = (typeof CARD_FACE_FOOTER_FIELD_IDS)[number]

export type FooterFieldVisibility = Partial<Record<FooterFieldId, boolean>>

export function footerFieldOn(
  fields: FooterFieldVisibility | undefined,
  id: FooterFieldId,
): boolean {
  return fields?.[id] !== false
}

export interface FooterSignals {
  attachmentCount: number
  relatedEmailCount: number
  childCount: number
  mergedCount: number
  presenceCount: number
  rollupCount: number
  hasWorkbook: boolean
  shouldRender: boolean
}

export function readFooterSignals(args: {
  card: KanbanCardModel
  isCompact: boolean
  emailMeta: KanbanEmailCardMeta | undefined
  assignedMembers: KanbanMember[]
  checklistTotal: number
  commentCount: number
  relatedCount: number
  unseenCount: number
  trailingSlot?: ReactNode
  fields?: FooterFieldVisibility
}): FooterSignals {
  const { card, isCompact, emailMeta, assignedMembers, fields } = args
  const on = (id: FooterFieldId) => footerFieldOn(fields, id)
  const attachmentCount = on('attachments')
    ? (card.attachment_count ?? card.related_attachment_ids_json?.length ?? 0)
    : 0
  const relatedEmailCount = on('replies')
    ? (card.related_email_count ?? card.related_emails?.length ?? 0)
    : 0
  const childCount = on('subcards') ? (card.child_count ?? 0) : 0
  const mergedCount = on('merged') ? (card.merged_card_count ?? 0) : 0
  const presenceCount = on('presence') ? 1 + (card.lane_placements?.length ?? 0) : 1
  const rollupCount = on('rollup') ? (card.entry_rollup?.count ?? 0) : 0
  const hasWorkbook = on('workbook') && (card.document_kinds ?? []).includes('workbook')
  const shouldRender = Boolean(
    (on('mail') && card.primary_email) ||
      (on('description') && card.summary) ||
      attachmentCount > 0 ||
      relatedEmailCount > 0 ||
      (on('checklist') && args.checklistTotal > 0) ||
      (on('comments') && args.commentCount > 0) ||
      childCount > 0 ||
      (on('related') && args.relatedCount > 0) ||
      (on('unseen') && args.unseenCount > 0) ||
      mergedCount > 0 ||
      presenceCount > 1 ||
      rollupCount > 0 ||
      hasWorkbook ||
      (on('personalCopy') && card.is_personal_mirror) ||
      (on('priority') && card.priority >= 1) ||
      (on('actionDate') && !!card.action_date) ||
      (on('due') && !!card.due_date) ||
      (on('snooze') && card.status === 'SNOOZED') ||
      (!isCompact && on('jobRef') && !!cardReferenceChip(card)) ||
      (!isCompact && on('assignees') && assignedMembers.length > 0) ||
      (!isCompact && on('mailbox') && !!emailMeta?.mailbox_address) ||
      !!args.trailingSlot,
  )
  return {
    attachmentCount,
    relatedEmailCount,
    childCount,
    mergedCount,
    presenceCount,
    rollupCount,
    hasWorkbook,
    shouldRender,
  }
}
